// Import React hooks and utilities
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Navigation between routes
import { useAuth } from '../hooks/useAuth'; // Custom hook for authentication
import axiosClient from '../api/axiosClient'; // Pre-configured Axios instance for API requests

// Define the structure of a Course object
interface Course {
  id: number;
  name: string;
  slug: string;
  enrolled: boolean; // Indicates if the user is enrolled in this course
}
// Component for selecting a course chat
function ChatSelection() {
  // Extract authenticated user and loading state from the custom hook
  const { user, isLoading } = useAuth();
  const navigate = useNavigate(); // React Router navigation hook
  // State to store fetched courses
  const [courses, setCourses] = useState<Course[]>([]);
  // State to store any error messages
  const [error, setError] = useState<string | null>(null);
  // Side effect: fetch courses when user authentication state changes
  useEffect(() => {
    // Redirect unauthenticated users to login once loading is complete
    if (!isLoading && !user) {
      navigate('/login');
      return;
    }
    // Fetch courses from the API
    const fetchCourses = async () => {
      try {
        // Request all courses from backend
        const response = await axiosClient.get('/elearning/api/courses/', {
          headers: {
            // Pass CSRF token for security (Django backend)
            'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
          },
        });
        // Ensure response is an array
        const coursesData = Array.isArray(response.data) ? response.data : [];
        let filteredCourses: Course[] = [];
        // Handle teacher role → show only their own courses
        if (user?.role === 'teacher') {
          filteredCourses = coursesData
            .filter((course: any) => course?.teacher && Number(course.teacher.id) === user.id)
            .map((course: any) => ({
              id: Number(course.id),
              name: course.name || '',
              slug: course.slug || `course-${course.id}`, // Fallback slug
              enrolled: false, // Teachers aren’t “enrolled” but can still access
            }));
        }
        // Handle student role → show only enrolled courses
        else if (user?.role === 'student') {
          let enrolledCourseIds: number[] = [];
          try {
            // Request enrollment records for the user
            const enrollmentsResponse = await axiosClient.get('/elearning/api/enrollments/', {
              headers: {
                'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
              },
            });
            // Extract course IDs the student is enrolled in
            enrolledCourseIds = Array.isArray(enrollmentsResponse.data)
              ? enrollmentsResponse.data
                  .filter((e: any) => e.course && (typeof e.course.id === 'number' || typeof e.course.id === 'string'))
                  .map((e: any) => Number(e.course.id))
              : [];
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (enrollErr: any) {
            // Do nothing
          }
          // Filter only courses where the student is enrolled
          filteredCourses = coursesData
            .filter((course: any) => course && course.id && enrolledCourseIds.includes(Number(course.id)))
            .map((course: any) => ({
              id: Number(course.id),
              name: course.name || '',
              slug: course.slug || `course-${course.id}`,
              enrolled: true,
            }));
        }
        // Update state with processed courses
        setCourses(filteredCourses);
        setError(null);
      } catch (err: any) {
        // Handle API errors gracefully
        const errorMessage = err.response?.data?.error
          ? JSON.stringify(err.response.data.error, null, 2)
          : err.message || 'Failed to load courses';
        setError(errorMessage);
      }
    };
    // Execute the async fetch function
    fetchCourses();
  }, [isLoading, user, navigate]); // Dependencies: re-run if user or auth state changes
  // Show loading spinner while authentication state is resolving
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // If no user after loading, render nothing
  if (!user) return null;
  // -----------------------------
// Modernized Course Chat Selection UI
// -----------------------------
return (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">

      {/* Page title */}
      <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-10 tracking-tight">
        💬 Select a Course Chat
      </h1>

      {/* Navigation buttons */}
      <div className="mb-10 flex flex-wrap gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
        >
          Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
        >
          Back to Homepage
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Course list */}
      {courses.length === 0 ? (
        <p className="text-gray-500 italic">No enrolled courses available.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-lg border border-gray-200 transition-transform transform hover:-translate-y-1"
            >
              {/* Course name */}
              <h2 className="text-lg font-semibold text-gray-900 mb-4 truncate">
                {course.name}
              </h2>

              {/* Chat link */}
              <a
                href={`/chat/${course.slug}`}
                className="inline-block w-full text-center px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
              >
                Join Chat
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

}
// Export component for use in the app
export default ChatSelection;